(() => {
  // ---------- Storage ----------
  function getStorageKey(picker) {
    const productId =
      picker?.dataset?.productId ||
      document.querySelector("variant-picker")?.dataset?.productId ||
      document.querySelector("[data-product-id]")?.getAttribute("data-product-id") ||
      "product";

    return `aqbeds_variant_tab_${productId}`;
  }

  /**
   * Storage key for a panel's saved colour. Scoped by product id so colours chosen on
   * one product are never restored onto a different product that happens to share the
   * same panel index.
   */
  function getColorStorageKey(picker, idx) {
    const productId =
      picker?.dataset?.productId ||
      document.querySelector("variant-picker")?.dataset?.productId ||
      "product";

    return `aqbeds_selected_color_${productId}_${idx}`;
  }

  function saveActiveTab(picker, index) {
    try {
      const key = getStorageKey(picker);
      localStorage.setItem(
        key,
        JSON.stringify({
          index: String(index),
          timestamp: Date.now(),
        })
      );
    } catch (e) {}
  }

  function loadActiveTab(picker) {
    try {
      const key = getStorageKey(picker);
      const saved = localStorage.getItem(key);
      if (!saved) return null;

      const data = JSON.parse(saved);
      if (!data || data.index == null) return null;

      // expire after 7 days
      if (Date.now() - (data.timestamp || 0) > 7 * 24 * 60 * 60 * 1000) {
        localStorage.removeItem(key);
        return null;
      }

      return String(data.index);
    } catch (e) {
      return null;
    }
  }

  // ---------- Core ----------
  function setActiveTab(picker, index, { persist = true } = {}) {
    if (!picker) return;

    const tabs = picker.querySelectorAll(".variant-tab[data-option-index]");
    const panels = picker.querySelectorAll(".variant-tab-panel[data-option-panel]");

    if (!tabs.length || !panels.length) return;

    const idx = String(index);

    // validate index
    const validTab = picker.querySelector(
      `.variant-tab[data-option-index="${idx}"]`
    );

    const finalIdx = validTab
      ? idx
      : String(tabs[0].getAttribute("data-option-index") || "0");

    if (persist) {
      saveActiveTab(picker, finalIdx);
    }

    // Update tabs
    tabs.forEach((t) => {
      const active = t.getAttribute("data-option-index") === finalIdx;
      t.classList.toggle("is-active", active);
      t.setAttribute("aria-selected", active ? "true" : "false");
      t.setAttribute("tabindex", active ? "0" : "-1");
    });

    // Update panels
    panels.forEach((p) => {
      const active = p.getAttribute("data-option-panel") === finalIdx;
      p.classList.toggle("is-active", active);
      if (active) p.removeAttribute("hidden");
      else p.setAttribute("hidden", "");
    });
  }

  /**
   * Restore the saved colour selection for every panel that has a colour picker.
   * A section morph resets all radios to the server default (first colour `checked`),
   * so we re-check the user's previously saved colour here. The visual `.selected`
   * class and tab labels are applied afterwards by `updateTabSelectedTextFromInputs`.
   */
  function restoreAllSavedColors(picker) {
    if (!picker) return;

    picker
      .querySelectorAll(".variant-tab-panel[data-option-panel]")
      .forEach((panel) => {
        // Only colour panels carry `.custom-color-option` inputs. Skip every other
        // panel (size / headboard / base) up front: those are variant options restored
        // server-side on morph, and their saved values can contain characters
        // (e.g. headboard `54" Inches`) that used to break the lookup below and abort
        // the whole loop — which is why the colour silently reset to the first swatch.
        const colorInputs = panel.querySelectorAll("input.custom-color-option");
        if (!colorInputs.length) return;

        const idx = panel.getAttribute("data-option-panel");

        let savedColor = null;
        try {
          savedColor = localStorage.getItem(getColorStorageKey(picker, idx));
        } catch (e) {
          return;
        }
        if (!savedColor) return;

        // Match by `.value` directly instead of building a CSS attribute selector, so
        // colour names containing quotes / brackets / backslashes can't throw or miss.
        let target = null;
        colorInputs.forEach((input) => {
          if (target) return;
          if (input.value === savedColor) target = input;
        });
        if (!target) return;

        colorInputs.forEach((input) => {
          input.checked = false;
        });
        target.checked = true;
      });
  }

  /**
   * Guarantee that every colour panel has exactly one checked swatch. The server renders
   * the first colour as `checked`, but a morph or a restore edge-case can leave a panel
   * with no checked radio — which would make the tab label read empty. When that happens,
   * fall back to the first swatch so a name is always shown.
   */
  function ensureColorSelection(picker) {
    if (!picker) return;

    picker
      .querySelectorAll(".variant-tab-panel[data-option-panel]")
      .forEach((panel) => {
        const colorInputs = panel.querySelectorAll("input.custom-color-option");
        if (!colorInputs.length) return;
        if (panel.querySelector("input.custom-color-option:checked")) return;
        colorInputs[0].checked = true;
      });
  }

  function updateTabSelectedTextFromInputs(picker) {
    if (!picker) return;

    const panels = picker.querySelectorAll(".variant-tab-panel[data-option-panel]");

    panels.forEach((panel) => {
      const idx = panel.getAttribute("data-option-panel");
      const tab = picker.querySelector(
        `.variant-tab[data-option-index="${idx}"]`
      );
      const selectedTextEl = tab?.querySelector("[data-selected-text]");

      if (!tab || !selectedTextEl) return;

      const checkedRadio = panel.querySelector(
        'input[type="radio"]:checked'
      );
      const select = panel.querySelector("select");

      // Clear selected class
      panel
        .querySelectorAll(".color-grid-item")
        .forEach((item) => item.classList.remove("selected"));

      if (checkedRadio) {
        selectedTextEl.textContent = checkedRadio.value || "";

        // Only persist selections for colour panels (those with `.custom-color-option`
        // inputs). Variant options are restored server-side on morph; storing their
        // values here would keep strings like `54" Inches` that can break restoration.
        if (panel.querySelector("input.custom-color-option")) {
          try {
            localStorage.setItem(
              getColorStorageKey(picker, idx),
              checkedRadio.value
            );
          } catch (e) {}
        }

        const gridItem = checkedRadio.closest(".color-grid-item");
        if (gridItem) {
          gridItem.classList.add("selected");
        }
      } else if (select) {
        selectedTextEl.textContent =
          select.options[select.selectedIndex]?.textContent?.trim() || "";
      }
    });
  }

  function initOrRestore(picker) {
    if (!picker) return;

    const fromStorage = loadActiveTab(picker);
    const index = fromStorage ?? "0";

    setActiveTab(picker, index, { persist: false });
    restoreAllSavedColors(picker);
    ensureColorSelection(picker);
    updateTabSelectedTextFromInputs(picker);
  }

  // ---------- Runner ----------
  document.addEventListener("DOMContentLoaded", () => {
    const picker =
      document.querySelector("variant-picker") ||
      document.querySelector("[data-variant-picker]") ||
      document.body;

    // Initial restore
    setTimeout(() => initOrRestore(picker), 50);

    // Tab click (event delegation)
    document.addEventListener("click", (e) => {
      const tab = e.target.closest(".variant-tab[data-option-index]");
      if (!tab) return;

      const p = tab.closest("variant-picker") || picker;
      const idx = tab.getAttribute("data-option-index");
      if (idx == null) return;

      setActiveTab(p, idx, { persist: true });
      // Refresh the selected-option labels whenever a tab is opened, so a colour name
      // can never appear blank right after a re-render left the panel without a selection.
      ensureColorSelection(p);
      updateTabSelectedTextFromInputs(p);
    });

    // Option change
    document.addEventListener("change", (e) => {
      const target = e.target;

      if (
        target.matches('.variant-tab-panel input[type="radio"]') ||
        target.matches(".variant-tab-panel select")
      ) {
        const p = target.closest("variant-picker") || picker;
        updateTabSelectedTextFromInputs(p);
      }
    });

    // Debounced MutationObserver (prevents flicker)
    let mutationTimeout = null;

    const observer = new MutationObserver(() => {
      clearTimeout(mutationTimeout);
      mutationTimeout = setTimeout(() => {
        initOrRestore(picker);
      }, 80);
    });

    observer.observe(picker, {
      childList: true,
      subtree: true,
    });

    // Variant events (Horizon safe)
    document.addEventListener("variant:change", () =>
      setTimeout(() => initOrRestore(picker), 50)
    );
    document.addEventListener("variant:changed", () =>
      setTimeout(() => initOrRestore(picker), 50)
    );
    document.addEventListener("product:variant-change", () =>
      setTimeout(() => initOrRestore(picker), 50)
    );

    // `variant:update` is dispatched by the theme the instant a section morph completes.
    // Restoring synchronously here (rather than via the debounced MutationObserver)
    // prevents the brief flash where the morphed server HTML shows the wrong active
    // tab / default colour before the saved state is reapplied.
    document.addEventListener("variant:update", () => initOrRestore(picker));
  });
})();
